import React from 'react';
import { FormattedMessage } from 'react-intl';

import { CrownAuthShell, NamedLink } from '../../components';
import { captureCfDestination } from '../../util/cfOrder';

import css from './NewListingCfOrderPrompt.module.css';

/**
 * Story 3.22 — what a SIGNED-OUT ClickFunnels buyer sees at
 * `/l/new?cf_order=…` instead of a blank page.
 *
 * 🔴 WHY THIS RENDERS AT ALL, WHEN THE RESPONSE IS STILL A 401.
 *
 * `server/index.js:368` sends the 401 with a fully rendered document attached.
 * Until this component existed, the thing rendered into that document was
 * `<NamedRedirect>` → react-router `<Redirect>`, which under `StaticRouter`
 * renders `null` and writes `context.url` — and `server/index.js` tests
 * `context.unauthorized` FIRST, so that redirect was computed and thrown away.
 * The buyer received a 401 whose `<div id="root">` was empty: 9.5 KB of
 * scaffolding, zero user-visible strings, no mention of the payment. 66 such
 * responses went to paying buyers in the 14-day retained window, across 43
 * orders and 42 people. One of them read the blank page as "my password is
 * wrong", requested two password-reset tokens for a password that was never
 * wrong, moved to a second device, and paid $20 twice for one horse.
 *
 * 🔴 THE 401 IS CORRECT AND IS NOT WHAT THIS FIXES. `staticContext.unauthorized`
 * is still set unconditionally, upstream in `Routes.js`, before this component
 * is chosen. This changes what the 401 SAYS, never what it IS.
 *
 * ⚠️ NOTHING HERE MAY TOUCH `window` UNGUARDED, DIRECTLY OR THROUGH A
 * DEPENDENCY. This renders on the SSR pass, where a `ReferenceError` thrown
 * from a render is a blank page — the exact defect being fixed, made permanent
 * and status-500. There is deliberately no `useEffect` and no data load: the
 * MARKUP is a pure function of `from` and the intl context, so the server and
 * the hydrated client produce the same tree.
 *
 * Story 3.24 — the ONE side effect, and why it sits in the render body.
 * `captureCfDestination(from)` writes the destination to localStorage (with a
 * 60-minute expiry — pass A moved it out of sessionStorage so it reaches the
 * new tab a password-reset link opens). It is
 * `hasWindow()`-guarded inside `cfOrder.js` (a no-op on the server, exactly
 * like the order-id capture) and it changes no markup, so the SSR/hydration
 * contract above still holds. It runs at RENDER, not on click, so it is
 * already stored when a click lands before the app has booted — and so it is
 * there for every path that drops router state: a password reset, a
 * Google/Facebook error, the migration modal's signup link, a full reload.
 * Last write wins and each render restarts its 60-minute clock — unlike the
 * order id, which is first-write-wins (see `captureCfDestination`).
 *
 * ⚠️ THE ORDER ID IS NEVER RENDERED. It is a bearer value (`logRedaction.js:121`
 * — "Presenting one binds a PAID slot"), and echoing it into the DOM would put
 * it into screenshots, session replay and support tickets. It rides `state.from`
 * on the two links and nowhere else.
 *
 * Structure follows `ClaimSlotPage`'s state A — the will-call screen written for
 * the same cohort — not a new idiom: the shell, a sub-line, and the same two
 * `NamedLink` CTAs carrying `state.from`.
 *
 * 🔴 THE CTA LABELS ARE THIS SCREEN'S OWN KEYS, NOT `ClaimSlotPage`'s. They were
 * reused verbatim in the first pass and the step-5 cold review (F3) rejected it:
 * the two screens live in different containers, and a copy edit to
 * `/claim/:token`'s buttons would have silently rewritten this purchase-path
 * screen with the whole suite still green, because the test read the same shared
 * key. The English is deliberately identical — the register matches (§2e) — but
 * the two now move independently. One string with two meanings is the shape that
 * shipped the tier slug/label defect to customers (Ref §1).
 *
 * @component
 * @param {Object} props
 * @param {string} props.from - `${pathname}${search}${hash}`, built ONCE in
 *   `Routes.js` and shared with the `<NamedRedirect>` arm, so the two can never
 *   drift. `AuthenticationPage.js:318` reads it back as `location.state?.from`
 *   and `:462-464` redirects to it once the buyer authenticates — which is the
 *   only carrier the query string has on the login tab, since
 *   `AuthenticationPage.js:337` gates the `sessionStorage` backup on
 *   `tab !== 'login'`. Dropping it would leave a buyer who signs up WORSE off
 *   than the blank page did: on the landing page, with her order id gone.
 * @returns {JSX.Element}
 */
const NewListingCfOrderPrompt = props => {
  const { from } = props;
  const fromState = { from };

  // Story 3.24 — remembered at render time (see the header). The router state
  // on the two links below is still the PRIMARY carrier; this is the backup
  // that survives the paths which lose it.
  captureCfDestination(from);

  return (
    <CrownAuthShell
      backdrop="cream"
      eyebrow="PREMIER HORSE"
      headline={<FormattedMessage id="NewListingPage.cfOrderHeading" />}
    >
      <div className={css.screen}>
        <p className={css.sub}>
          <FormattedMessage id="NewListingPage.cfOrderBody" />
        </p>
        <div className={css.actions}>
          {/* SignupPage is where the client redirect already went — `/l/new`
              sets no `authPage`, so `Routes.js:177`'s default applied. The
              destination is unchanged; only the blank second of getting there
              is. */}
          <NamedLink name="SignupPage" to={{ state: fromState }} className={css.primaryButton}>
            <FormattedMessage id="NewListingPage.cfOrderCreateAccount" />
          </NamedLink>
          <NamedLink name="LoginPage" to={{ state: fromState }} className={css.secondaryLink}>
            <FormattedMessage id="NewListingPage.cfOrderHaveAccount" />
          </NamedLink>
        </div>
      </div>
    </CrownAuthShell>
  );
};

export default NewListingCfOrderPrompt;
